import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { StoreHeader } from "../components/StoreHeader";
import { StoreFooter } from "../components/StoreFooter";
import { orderApi } from "../lib/api";
import { toast } from "sonner";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function TrackOrderPage() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const res = await orderApi.getById(orderNumber);
        setOrder(res.data);
      } catch (error) {
        toast.error("Order not found");
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderNumber]);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-stone-900 mb-6">
          Track Your Order
        </h1>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : !order ? (
          <div className="bg-white border border-stone-200 p-8 text-center">
            <h2 className="text-xl font-bold text-stone-900 mb-2">
              Order not found
            </h2>
            <p className="text-stone-500">
              Please check your order number and try again.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 overflow-hidden">
            <div className="bg-stone-50 p-4 border-b border-stone-200 flex justify-between">
              <div>
                <p className="text-sm text-stone-500">Order Number</p>
                <p className="font-mono font-bold text-emerald-900">
                  {order.order_number}
                </p>
                <p className="text-sm text-stone-500 mt-2">Date</p>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
              <Badge className={statusColors[order.order_status]}>
                {order.order_status.toUpperCase()}
              </Badge>
            </div>

            <div className="p-4">
              <p className="font-medium mb-2">Items</p>
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm mb-2">
                  <span>
                    {item.product_name} (Qty: {item.quantity})
                  </span>
                  <span>KES {item.total_price.toLocaleString()}</span>
                </div>
              ))}

              <div className="mt-4 pt-4 border-t border-stone-200 text-sm">
                <p>
                  <span className="font-medium text-stone-700">Delivery:</span>{" "}
                  {order.delivery_address}
                </p>
                <p className="mt-1">
                  <span className="font-medium text-stone-700">Payment:</span>{" "}
                  {order.payment_method === "mpesa"
                    ? "M-Pesa"
                    : "Cash on Delivery"}
                </p>
                <p className="mt-1">
                  <span className="font-medium text-stone-700">Total:</span> KES{" "}
                  {order.total_amount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      <StoreFooter />
    </div>
  );
}
